import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Lock, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { resetPasswordThunk } from '../../redux/authentication/utils/core/authRecoveryActions';
import { Input } from '../../components/ui';

// Mirrors RegisterPage.jsx's validatePasswordMsg -- same backend complexity
// rule (UserCreate.validate_password): 8+ chars, a letter, a digit, and a
// special character. A reset that accepted a weaker password than register
// requires would just fail the same check on next login, so this needs to
// match exactly, not just be "reasonably strict."
const validatePasswordMsg = (p, t) => {
  if (!p) return t('auth.register.errors.passwordRequired');
  if (p.length < 8) return t('auth.register.errors.passwordTooShort');
  if (!/[A-Za-z]/.test(p) || !/[0-9]/.test(p) || !/[^A-Za-z0-9]/.test(p)) {
    return t('auth.register.errors.passwordComplexity');
  }
  return '';
};

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, successMessage, errorMessage } = useSelector((state) => state.authRecovery);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [touched, setTouched] = useState({ password: false, confirmPassword: false });
  const [localError, setLocalError] = useState('');

  const passwordError = touched.password ? validatePasswordMsg(password, t) : '';
  const confirmError =
    touched.confirmPassword && confirmPassword && password !== confirmPassword
      ? t('auth.resetPassword.errors.passwordsMismatch')
      : '';

  // A successful reset used to just sit there showing a green message with
  // no next step -- send the user on to log in with the new password.
  useEffect(() => {
    if (!successMessage) return;
    const id = setTimeout(() => navigate('/login'), 2000);
    return () => clearTimeout(id);
  }, [successMessage, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched({ password: true, confirmPassword: true });

    const pMsg = validatePasswordMsg(password, t);
    if (pMsg) {
      setLocalError(pMsg);
      return;
    }
    if (password !== confirmPassword) {
      setLocalError(t('auth.resetPassword.errors.passwordsMismatch'));
      return;
    }

    setLocalError('');
    dispatch(resetPasswordThunk({ token, newPassword: password }));
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-4 py-12 relative overflow-hidden bg-gray-50 dark:bg-[#09090f]"
      style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');`}</style>

      {/* Top radial glow — identical to the homepage hero, dark-only by design
          (same reasoning as LoginPage.jsx: a light-mode wash of this violet
          at this size/opacity reads as a stain, not a glow). */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[480px] pointer-events-none hidden dark:block"
        style={{ background: 'radial-gradient(ellipse at 50% -10%, rgba(109,40,217,0.16) 0%, transparent 65%)' }}
      />

      <button
        onClick={() => navigate('/login')}
        className="absolute top-6 left-6 flex items-center text-violet-600 dark:text-violet-300 hover:text-violet-500 dark:hover:text-violet-100 transition-colors z-50"
      >
        <ArrowLeft className="w-5 h-5 mr-1" /> {t('auth.back')}
      </button>

      <div className="relative w-full max-w-md z-10">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{t('auth.resetPassword.heading')}</h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">{t('auth.resetPassword.subtitle')}</p>
        </div>

        <div className="rounded-3xl p-8 shadow-2xl bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08]">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <Input
              icon={Lock}
              isPassword
              placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => setTouched((s) => ({ ...s, password: true }))}
              autoComplete="new-password"
              error={passwordError}
            />

            <Input
              icon={Lock}
              isPassword
              placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onBlur={() => setTouched((s) => ({ ...s, confirmPassword: true }))}
              autoComplete="new-password"
              error={confirmError}
            />

            {localError && (
              <div className="p-3 rounded-lg text-sm border bg-red-50 dark:bg-red-600/30 border-red-300 dark:border-red-500 text-red-700 dark:text-red-200">
                {localError}
              </div>
            )}
            {successMessage && <p className="text-green-600 dark:text-green-400 text-sm">{successMessage}</p>}
            {errorMessage && (
              <div className="p-3 rounded-lg text-sm border bg-red-50 dark:bg-red-600/30 border-red-300 dark:border-red-500 text-red-700 dark:text-red-200">
                {errorMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-2xl shadow-lg disabled:opacity-50 transition-all duration-200 active:scale-[0.98]"
            >
              {loading ? t('auth.resetPassword.resetting') : t('auth.resetPassword.submit')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
